import { useState, FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

export default function AuthPage({ mode }: { mode: "login" | "signup" }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: params.get("name") || "", email: params.get("email") || "", password: "", country: "UK" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const isLogin = mode === "login";

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post(isLogin ? "/auth/login" : "/auth/register", isLogin ? { email: form.email, password: form.password } : form);
      setUser(data);
      navigate(data.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const field = "h-12 w-full rounded-xl border border-brand-line bg-white px-4 text-sm outline-none ring-brand-blue/30 transition focus:ring-4";

  return (
    <div className="grid min-h-screen lg:grid-cols-[1fr_1fr]" data-testid={`${mode}-page`}>
      <div className="relative hidden grain bg-brand-navy p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <Link to="/"><Logo dark /></Link>
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-brand-gold">Family portal</p>
          <h1 className="mt-4 max-w-md font-heading text-4xl font-extrabold leading-tight">Every visit, verified. Every pound, protected.</h1>
          <p className="mt-4 max-w-md text-slate-300">Add your loved one, book care in Harare or Bulawayo, and watch check-ins arrive from WhatsApp in real time.</p>
        </div>
        <p className="text-xs text-slate-400">Bhasa Diaspora Healthcare Pvt Ltd</p>
      </div>
      <div className="flex items-center justify-center px-4 py-12 sm:px-8">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-10 inline-block lg:hidden"><Logo /></Link>
          <h2 className="font-heading text-3xl font-bold text-brand-navy">{isLogin ? "Welcome back" : "Create your family account"}</h2>
          <p className="mt-2 text-sm text-brand-slate">{isLogin ? "Log in to manage care for your family." : params.get("email") ? "Welcome from the waitlist — bookings are open. Set a password to finish." : "Takes a minute. No card needed until you book."}</p>

          <button onClick={googleLogin} type="button" className="mt-8 flex h-12 w-full items-center justify-center gap-3 rounded-xl border border-brand-line bg-white text-sm font-semibold text-brand-navy transition hover:bg-slate-50" data-testid="google-login-button">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.8 6C12.4 13.6 17.7 9.5 24 9.5z" /><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4 7.1-10 7.1-17.5z" /><path fill="#FBBC05" d="M10.5 28.7a14.5 14.5 0 0 1 0-9.4l-7.8-6a24 24 0 0 0 0 21.4l7.8-6z" /><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.9 2.3-8.4 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.8 6C6.6 42.6 14.6 48 24 48z" /></svg>
            Continue with Google
          </button>
          <div className="my-6 flex items-center gap-4 text-xs uppercase tracking-widest text-brand-slate"><span className="h-px flex-1 bg-brand-line" />or email<span className="h-px flex-1 bg-brand-line" /></div>

          <form onSubmit={submit} className="space-y-4" data-testid={`${mode}-form`}>
            {!isLogin && (
              <>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className={field} data-testid="signup-name-input" />
                <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={field} data-testid="signup-country-select">
                  <option value="UK">I live in the UK</option><option value="US">I live in the US</option><option value="Other">Elsewhere</option>
                </select>
              </>
            )}
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" className={field} data-testid={`${mode}-email-input`} />
            <input required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={isLogin ? "Password" : "Password (min 8 characters)"} className={field} data-testid={`${mode}-password-input`} />
            {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="auth-error">{error}</p>}
            <button type="submit" disabled={loading} className="flex h-12 w-full items-center justify-center rounded-xl bg-brand-blue text-sm font-semibold text-white transition hover:bg-brand-blueHover disabled:opacity-60" data-testid={`${mode}-submit-button`}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : isLogin ? "Log in" : "Create account"}
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-brand-slate">
            {isLogin ? <>New to Bhasa? <Link to="/signup" className="font-semibold text-brand-blue" data-testid="goto-signup-link">Create an account</Link></> : <>Already have an account? <Link to="/login" className="font-semibold text-brand-blue" data-testid="goto-login-link">Log in</Link></>}
          </p>
        </div>
      </div>
    </div>
  );
}
